import {
  AuthFlowType,
  ChangePasswordCommand,
  CognitoIdentityProviderClient,
  ConfirmForgotPasswordCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  InitiateAuthCommand,
  SignUpCommand,
  UpdateUserAttributesCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { logger } from '../utils/logger';

// Cognito configuration from environment variables
const REGION = process.env.REACT_APP_AWS_REGION || 'us-east-1';
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || '';

if (!CLIENT_ID) {
}

const cognitoClient = new CognitoIdentityProviderClient({ region: REGION });

export interface SignUpData {
  email: string;
  password: string;
  givenName: string;
  familyName: string;
  ageRange: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface AuthResult {
  success: boolean;
  message: string;
  requiresConfirmation?: boolean;
  userSub?: string;
}

export interface CognitoError {
  name?: string;
  message?: string;
}

// Check for fake emails
const isFakeEmail = (email: string): boolean => {
  const fakePatterns = [
    /^test@/i,
    /^fake@/i,
    /^asdasd@/i,
    /^123@/i,
    /^abc@/i,
    /^temp@/i,
    /^dummy@/i,
    /^demo@/i,
    /^sample@/i,
    /^example@/i,
    /^admin@/i,
    /^user@/i,
    /^guest@/i,
    /@test\./i,
    /@fake\./i,
    /@example\./i,
    /@localhost/i,
    /@temp\./i,
    /@dummy\./i,
    /@demo\./i,
    /@sample\./i,
    /@testing\./i,
    /@dev\./i,
    /@staging\./i,
  ];

  return (
    fakePatterns.some((pattern) => pattern.test(email)) ||
    email.includes('asdasd') ||
    email.includes('qwerty') ||
    email.includes('123456')
  );
};

// Sign up a new user
export const signUp = async (data: SignUpData): Promise<AuthResult> => {
  try {
    // DOUBLE CHECK: Reject fake emails at service level
    if (isFakeEmail(data.email)) {
      return {
        success: false,
        message: 'Please use a real email address',
      };
    }

    const command = new SignUpCommand({
      ClientId: CLIENT_ID,
      Username: data.email,
      Password: data.password,
      UserAttributes: [
        {
          Name: 'email',
          Value: data.email,
        },
        {
          Name: 'given_name',
          Value: data.givenName,
        },
        {
          Name: 'family_name',
          Value: data.familyName,
        },
        {
          Name: 'custom:age_range',
          Value: data.ageRange,
        },
      ],
    });

    const response = await cognitoClient.send(command);

    return {
      success: true,
      message: 'Account created successfully! Please check your email for verification code.',
      requiresConfirmation: true,
      userSub: response.UserSub,
    };
  } catch (error: unknown) {
    const err = error as CognitoError;
    // Handle specific Cognito errors
    if (err.name === 'UsernameExistsException') {
      return {
        success: false,
        message: 'An account with this email already exists.',
      };
    } else if (err.name === 'InvalidPasswordException') {
      return {
        success: false,
        message: 'Password does not meet requirements.',
      };
    } else if (err.name === 'InvalidParameterException') {
      return {
        success: false,
        message: 'Invalid email or password format.',
      };
    }

    return {
      success: false,
      message: err.message || 'Sign up failed. Please try again.',
    };
  }
};

// Confirm sign up with verification code
export const confirmSignUp = async (email: string, code: string): Promise<AuthResult> => {
  try {
    const command = new ConfirmSignUpCommand({
      ClientId: CLIENT_ID,
      Username: email,
      ConfirmationCode: code,
    });

    await cognitoClient.send(command);

    return {
      success: true,
      message: 'Email verified successfully! You can now sign in.',
    };
  } catch (error: unknown) {
    const err = error as CognitoError;
    if (err.name === 'CodeMismatchException') {
      return {
        success: false,
        message: 'Invalid verification code. Please try again.',
      };
    } else if (err.name === 'ExpiredCodeException') {
      return {
        success: false,
        message: 'Verification code has expired. Please request a new one.',
      };
    }

    return {
      success: false,
      message: err.message || 'Verification failed. Please try again.',
    };
  }
};

// Sign in user
export const signIn = async (data: LoginData): Promise<AuthResult> => {
  try {
    const command = new InitiateAuthCommand({
      ClientId: CLIENT_ID,
      AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
      AuthParameters: {
        USERNAME: data.email,
        PASSWORD: data.password,
      },
    });

    const response = await cognitoClient.send(command);

    if (response.AuthenticationResult) {
      // Store tokens in localStorage
      localStorage.setItem('accessToken', response.AuthenticationResult.AccessToken || '');
      localStorage.setItem('idToken', response.AuthenticationResult.IdToken || '');
      localStorage.setItem('refreshToken', response.AuthenticationResult.RefreshToken || '');

      // Store token expiry time (ExpiresIn is in seconds)
      const expiresIn = response.AuthenticationResult.ExpiresIn || 3600;
      const expiresAt = Date.now() + expiresIn * 1000;
      localStorage.setItem('tokenExpiresAt', expiresAt.toString());

      // Store user info from ID token
      try {
        const idToken = response.AuthenticationResult.IdToken;
        if (idToken) {
          const payload = JSON.parse(atob(idToken.split('.')[1]));
          localStorage.setItem('userEmail', payload.email || '');
          localStorage.setItem('userId', payload['custom:user_id'] || payload.sub);
          localStorage.setItem(
            'displayName',
            payload['custom:display_name'] || payload.email?.split('@')[0] || '',
          );
        }
      } catch (_e) {}

      return {
        success: true,
        message: 'Signed in successfully!',
      };
    } else {
      logger.warn('CognitoService', 'SignIn: No AuthenticationResult in response', response);
      return {
        success: false,
        message: 'Sign in failed. Please try again.',
      };
    }
  } catch (error: unknown) {
    const err = error as CognitoError;
    if (err.name === 'NotAuthorizedException') {
      // Cognito returns "User is disabled" for soft-deleted accounts
      if (err.message?.toLowerCase().includes('user is disabled')) {
        return {
          success: false,
          message: 'USER_DISABLED',
        };
      }
      return {
        success: false,
        message: 'Invalid email or password.',
      };
    } else if (err.name === 'UserNotConfirmedException') {
      return {
        success: false,
        message: 'Email not confirmed. Please verify your email before signing in.',
      };
    } else if (err.name === 'UserNotFoundException') {
      return {
        success: false,
        message: 'No account found with this email. Please sign up first.',
      };
    } else if (err.message?.includes('USER_PASSWORD_AUTH flow not enabled')) {
      return {
        success: false,
        message: 'No account found with this email. Please sign up first.',
      };
    }

    return {
      success: false,
      message: err.message || 'Sign in failed. Please try again.',
    };
  }
};

// Sign out user
export const signOut = (): void => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('idToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('tokenExpiresAt');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userId');
  localStorage.removeItem('displayName');
  localStorage.removeItem('auth_code'); // Legacy

  // Clear session storage flags (e.g. age range prompt for federated sign-up)
  sessionStorage.removeItem('theychat-needs-age-range');
};

// Forgot Password - Send reset code to email
export const forgotPassword = async (email: string): Promise<AuthResult> => {
  try {
    const command = new ForgotPasswordCommand({
      ClientId: CLIENT_ID,
      Username: email,
    });

    await cognitoClient.send(command);

    return {
      success: true,
      message: 'Password reset code sent to your email. Please check your inbox.',
    };
  } catch (error: unknown) {
    const err = error as CognitoError;
    // Handle specific error cases
    if (err.name === 'UserNotFoundException') {
      return {
        success: false,
        message: 'UserNotFound',
      };
    }

    if (err.name === 'LimitExceededException') {
      return {
        success: false,
        message: 'Too many attempts. Please try again later.',
      };
    }

    return {
      success: false,
      message: err.message || 'Failed to send reset code. Please try again.',
    };
  }
};

// Confirm Forgot Password - Reset password with code
export const confirmForgotPassword = async (
  email: string,
  code: string,
  newPassword: string,
): Promise<AuthResult> => {
  try {
    const command = new ConfirmForgotPasswordCommand({
      ClientId: CLIENT_ID,
      Username: email,
      ConfirmationCode: code,
      Password: newPassword,
    });

    await cognitoClient.send(command);

    return {
      success: true,
      message: 'Password reset successful! You can now sign in with your new password.',
    };
  } catch (error: unknown) {
    const err = error as CognitoError;
    // Handle specific error cases
    if (err.name === 'CodeMismatchException') {
      return {
        success: false,
        message: 'Invalid verification code. Please check and try again.',
      };
    }

    if (err.name === 'ExpiredCodeException') {
      return {
        success: false,
        message: 'Verification code has expired. Please request a new one.',
      };
    }

    if (err.name === 'InvalidPasswordException') {
      return {
        success: false,
        message:
          'Password does not meet requirements. Must be at least 8 characters with uppercase, lowercase, and numbers.',
      };
    }

    if (err.name === 'LimitExceededException') {
      return {
        success: false,
        message: 'Too many attempts. Please try again later.',
      };
    }

    return {
      success: false,
      message: err.message || 'Failed to reset password. Please try again.',
    };
  }
};

// Update user profile attributes
export const updateUserProfile = async (
  attributes: { Name: string; Value: string }[],
): Promise<AuthResult> => {
  try {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      throw new Error('User is not authenticated');
    }

    const command = new UpdateUserAttributesCommand({
      AccessToken: accessToken,
      UserAttributes: attributes,
    });

    await cognitoClient.send(command);

    // Update local storage if display name changed
    const displayNameAttr = attributes.find((attr) => attr.Name === 'custom:display_name');
    if (displayNameAttr) {
      localStorage.setItem('displayName', displayNameAttr.Value);
    }

    return {
      success: true,
      message: 'Profile updated successfully!',
    };
  } catch (error: unknown) {
    const err = error as CognitoError;
    return {
      success: false,
      message: err.message || 'Failed to update profile. Please try again.',
    };
  }
};

// Change password
export const changePassword = async (
  oldPassword: string,
  newPassword: string,
): Promise<AuthResult> => {
  try {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      throw new Error('User is not authenticated');
    }

    const command = new ChangePasswordCommand({
      AccessToken: accessToken,
      PreviousPassword: oldPassword,
      ProposedPassword: newPassword,
    });

    await cognitoClient.send(command);

    return {
      success: true,
      message: 'Password changed successfully!',
    };
  } catch (error: unknown) {
    const err = error as CognitoError;
    if (err.name === 'InvalidPasswordException') {
      return {
        success: false,
        message:
          'Password does not meet requirements. Must be at least 8 characters with uppercase, lowercase, and numbers.',
      };
    }
    if (err.name === 'NotAuthorizedException') {
      return {
        success: false,
        message: 'Current password is incorrect.',
      };
    }
    if (err.name === 'LimitExceededException') {
      return {
        success: false,
        message: 'Too many attempts. Please try again later.',
      };
    }

    return {
      success: false,
      message: err.message || 'Failed to change password. Please try again.',
    };
  }
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  const accessToken = localStorage.getItem('accessToken');
  const idToken = localStorage.getItem('idToken');
  const tokenExpiresAt = localStorage.getItem('tokenExpiresAt');

  // Must have both tokens
  if (!accessToken || !idToken) {
    return false;
  }

  // Check if token is expired (with 5 minute buffer)
  if (tokenExpiresAt) {
    const expiryTime = parseInt(tokenExpiresAt, 10);
    const bufferMs = 5 * 60 * 1000; // 5 minutes
    if (Date.now() > expiryTime - bufferMs) {
      // Token is expired or about to expire
      // TODO: Implement token refresh
      return false;
    }
  }

  return true;
};

// Get the current user's ID token (for API calls)
export const getIdToken = (): string | null => {
  return localStorage.getItem('idToken');
};

// Check if the current user logged in via a federated provider (e.g. Google)
export const isFederatedUser = (): boolean => {
  const idToken = localStorage.getItem('idToken');
  if (!idToken) return false;

  try {
    const payload = JSON.parse(atob(idToken.split('.')[1]));

    // Cognito includes 'identities' claim for federated users
    if (payload.identities && Array.isArray(payload.identities) && payload.identities.length > 0) {
      return true;
    }

    // Fallback: federated users typically have a provider prefix in cognito:username
    const cognitoUsername: string = payload['cognito:username'] ?? '';
    if (cognitoUsername.startsWith('google_') || cognitoUsername.includes('Google')) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

// Get current user info from stored token
export const getCurrentUser = (): { email: string; userId: string; displayName: string } | null => {
  const idToken = localStorage.getItem('idToken');
  if (!idToken) return null;

  try {
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    return {
      email: payload.email || '',
      userId: payload['custom:user_id'] || payload.sub,
      displayName: payload['custom:display_name'] || payload.email?.split('@')[0] || '',
    };
  } catch {
    return null;
  }
};

// Request account recovery for soft-deleted accounts
export const requestAccountRecovery = async (
  email: string,
): Promise<{
  success: boolean;
  message: string;
  emailMasked?: string;
  daysRemaining?: number;
}> => {
  const { getApiUrl } = await import('../utils/api');
  const res = await fetch(`${getApiUrl()}/user/account/recover-request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || 'Recovery request failed');
  }
  return {
    success: true,
    message: data.message,
    emailMasked: data.email_masked,
    daysRemaining: data.days_remaining,
  };
};

// Confirm account recovery with code
export const confirmAccountRecovery = async (
  email: string,
  code: string,
): Promise<{ success: boolean; message: string }> => {
  const { getApiUrl } = await import('../utils/api');
  const res = await fetch(`${getApiUrl()}/user/account/recover`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, recovery_code: code }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || 'Recovery failed');
  }
  return { success: true, message: data.message };
};
